import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel, normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../shared/services/apis/produto/controle-estoque.service';
import { InventarioService } from '../../../shared/services/apis/suprimentos/inventario.service';
import { LookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { TipoContatoEnum } from '../../../shared/enums/pessoa/tipo-contato.enum';
import { OrigemConsultaControleEstoqueEnum } from '../../../shared/enums/produto/origem-consulta-controle-estoque.enum';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { TagListItem } from '../../../shared/services/structs/produto/tag-list-item.struct';
import { ControleEstoqueListaRequest } from '../../../shared/services/requests/produto/controle-estoque-lista.request';
import { ControleEstoqueInventarioPrintRequest } from '../../../shared/services/requests/produto/controle-estoque-inventario-print.request';
import { UtilService } from '../../../shared/services/util.service';
import { ImpressaoInventarioFiltrarProdutosComponent } from './dialogs/impressao-inventario-filtrar-produtos/impressao-inventario-filtrar-produtos.component';
import { EscolherFormatoContagemComponent, FormatoContagem } from './dialogs/escolher-formato-contagem/escolher-formato-contagem.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';

interface ProdutoLocalRow {
  idLocalizacaoEstoque: number;
  nomeLocalizacao: string;
  saldoFisico: number;
  saldoDisponivel: number;
}

interface ProdutoFiltradoRow {
  idProduto: number;
  descricao: string;
  codigoSku?: string;
  unidade?: string;
  custoMedio: number;
  isControlaLotes: boolean;
  isControlaSeries: boolean;
  localizacoes: string;
  saldoTotal: number;
  saldoDisponivelTotal: number;
  locais: ProdutoLocalRow[];
  isProdutoComVariacao: boolean;
  quantidadeVariacoes: number;
  variacoes: ProdutoFiltradoRow[];
}

interface DisplayRow {
  data: ProdutoFiltradoRow;
  variacao?: ProdutoFiltradoRow;
  local?: ProdutoLocalRow;
  isVariacao: boolean;
  isLocal: boolean;
}

const URLS_ORIGEM_FILTRO = [
  '/suprimentos/inventario',
  '/suprimentos/inventario/importar-planilha',
];

@Component({
  selector: 'app-inventario-filtrar-produtos',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatDialogModule,
    EmptyListComponent,
    FooterActionComponent,
    AbntPipe,
  ],
  templateUrl: './inventario-filtrar-produtos.component.html',
  styleUrl: './inventario-filtrar-produtos.component.css',
})
export class InventarioFiltrarProdutosComponent implements OnInit, OnDestroy, AfterViewInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_InventarioEstoque;

  public formFiltro: FormGroup;

  public listaLocalizacoes: LookupItemStruct[] = [];
  public listaCategoria: LookupItemStruct[] = [];
  public listaTag: TagListItem[] = [];
  public listaFornecedor: LookupItemStruct[] = [];

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) this._flatDataSource.sort = sort;
  }

  public dataSource: ProdutoFiltradoRow[] = [];
  public _flatDataSource = new DataSourceOrdenacaoInsensivel<DisplayRow>([]);
  public expandedElement: ProdutoFiltradoRow | null = null;
  public expandedVariacoes = new Set<ProdutoFiltradoRow>();

  public get displayedColumns(): string[] {
    return ['produto', 'sku', 'preco', 'unidade', 'localizacao', 'saldo'];
  }

  public tipoRefinamento = 0;

  public _isLoadingProdutos = false;
  public _isLoadingLocalizacoes = false;
  public _isPrinting = false;
  public _isExporting = false;

  public todosProdutos: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private controleEstoqueService: ControleEstoqueService,
    private lookupService: LookupService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private utilService: UtilService,
    private inventarioService: InventarioService,
  ) {
    this._flatDataSource.sortingDataAccessor = (row, property) => {
      switch (property) {
        case 'produto': return normalizarValorOrdenacao(row.data.descricao);
        case 'sku': return normalizarValorOrdenacao(row.data.codigoSku ?? '');
        case 'preco': return row.data.custoMedio;
        case 'unidade': return normalizarValorOrdenacao(row.data.unidade ?? '');
        case 'localizacao': return normalizarValorOrdenacao(this.getLocalizacaoLabel(row));
        case 'saldo': return this.getSaldo(row);
        default: return normalizarValorOrdenacao((row as Record<string, any>)[property]);
      }
    };
  }

  ngAfterViewInit(): void {
    // sort attached via @ViewChild setter
  }

  ngOnInit(): void {
    this.formFiltro = this.fb.group({
      busca: [''],
      listIdTag: [[]],
      idLocalizacaoEstoque: [null],
      idCategoria: [null],
      idSituacaoProduto: [1],
      idExibir: [0],
      idFornecedor: [null],
      idTipoValorBase: [1],
      apenasComEstoque: [false],
    });

    this.carregarLocalizacoes();
    this.carregarLookups();
    this.carregarFornecedores();
    this._restaurarFiltro();
  }

  ngOnDestroy(): void {
    if (this.formFiltro) {
      this.utilService.salvarFiltro(
        this.moduloEnum,
        this.funcionalidadeEnum,
        this.formFiltro,
        URLS_ORIGEM_FILTRO,
      );
    }
  }

  private _restaurarFiltro(): void {
    const salvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (salvo) {
      this.formFiltro.patchValue(salvo);
      this.filtrar();
    }
  }

  public get totalProdutos(): number {
    return this.dataSource.length;
  }

  public filtrar(): void {
    this._isLoadingProdutos = true;

    const { busca, idSituacaoProduto, idExibir } = this.formFiltro.value;

    const request: ControleEstoqueListaRequest = {
      origem: OrigemConsultaControleEstoqueEnum.InventarioEstoque,
      searchText: busca || undefined,
      idControleEstoqueListaRefinamento: idSituacaoProduto ?? 0,
      idTipoMovimentoFiltro: 0,
      idResumoFiltro: idExibir ?? 0,
    };

    forkJoin({
      produtos: this.controleEstoqueService.getAll(request, 0),
      bloqueados: this.inventarioService.getProdutosEmAberto().pipe(
        catchError(() => of({ isError: true, idsProduto: [] as number[] }))
      ),
    }).subscribe({
      next: ({ produtos, bloqueados }) => {
        this._isLoadingProdutos = false;
        if (produtos.isError) { this.alertService.show('Erro', produtos.errorDescription, AlertType.error); return; }

        // Regra de conflito: produtos em inventário aberto não devem ser listados para nova geração
        const idsBloqueados = new Set<number>(bloqueados.isError ? [] : bloqueados.idsProduto);
        const removerBloqueados = (lista: any[]): any[] =>
          (lista ?? [])
            .filter(p => !idsBloqueados.has(p.idProduto))
            .map(p => ({ ...p, variacoes: removerBloqueados(p.variacoes) }));

        this.todosProdutos = removerBloqueados(produtos.list ?? []);
        this.aplicarFiltroLocalizacao();
      },
      error: err => {
        this._isLoadingProdutos = false;
        this.alertService.show('Erro', err.message || err, AlertType.error);
      },
    });
  }

  public limparFiltros(): void {
    this.formFiltro.reset({
      busca: '',
      listIdTag: [],
      idLocalizacaoEstoque: null,
      idCategoria: null,
      idSituacaoProduto: 1,
      idExibir: 0,
      idFornecedor: null,
      idTipoValorBase: 1,
      apenasComEstoque: false,
    });
    this.todosProdutos = [];
    this.dataSource = [];
    this.expandedElement = null;
    this.expandedVariacoes.clear();
    this._reconstruirFlatDataSource();
  }

  public abrirEscolherFormato(): void {
    const ref = this.dialog.open(EscolherFormatoContagemComponent, {
      data: { totalProdutos: this.totalProdutos },
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((opcao?: FormatoContagem) => {
      if (opcao === 'imprimir') this.imprimirLista();
      else if (opcao === 'exportar') this.exportarPlanilha();
      else if (opcao === 'digitar') this.iniciarDigitacao();
    });
  }

  public imprimirLista(): void {
    if (this._isPrinting) return;
    const request = this._buildPrintRequest();

    const ref = this.dialog.open(ImpressaoInventarioFiltrarProdutosComponent, {
      data: {
        filtros: request,
        exibirSaldoAtual: !!this.formFiltro.value.apenasComEstoque,
      },
      maxWidth: '90vw',
      width: '1000px',
      disableClose: false,
    });

    ref.afterClosed().subscribe(() => {
      this._perguntarNavegacao(
        'Imprimir lista concluída',
        'Deseja ir para a tela de Inventário de estoque?',
        '/suprimentos/inventario',
      );
    });
  }

  public exportarPlanilha(): void {
    if (this._isExporting) return;
    this._isExporting = true;

    const request = this._buildPrintRequest();

    this.controleEstoqueService.exportarInventarioCsv(request).subscribe({
      next: blob => {
        this._isExporting = false;
        const dataStr = new Date().toISOString().slice(0, 10);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `inventario-produtos-${dataStr}.csv`;
        link.click();
        URL.revokeObjectURL(url);

        this._perguntarNavegacao(
          'Exportação concluída',
          'Deseja ir para a tela de Importação por planilha?',
          '/suprimentos/inventario/importar-planilha',
        );
      },
      error: err => {
        this._isExporting = false;
        this.alertService.show('Erro', err.message || 'Erro ao exportar planilha.', AlertType.error);
      },
    });
  }

  private _perguntarNavegacao(title: string, description: string, destinoUrl: string): void {
    const ref = this.dialog.open(ConfirmModalComponent, {
      width: '420px',
      data: {
        title,
        description,
        buttonConfirm: 'Sim, ir',
        buttonClose: 'Não, continuar',
      },
    });

    ref.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.router.navigateByUrl(destinoUrl);
      }
    });
  }

  public iniciarDigitacao(): void {
    this.router.navigate(['/suprimentos/inventario/novo'], {
      state: {
        fromFiltro: true,
        idLocalizacaoEstoque: this.formFiltro.value.idLocalizacaoEstoque ?? null,
        // A digitação conta por item: produtos com variação são achatados em suas variações
        // (cada uma com idProduto/SKU/locais próprios). A listagem acima permanece hierárquica.
        todosProdutos: this.achatarProdutos(this.todosProdutos),
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/suprimentos/inventario']);
  }

  private _buildPrintRequest(): ControleEstoqueInventarioPrintRequest {
    const { busca, idSituacaoProduto, idExibir, idLocalizacaoEstoque, apenasComEstoque } = this.formFiltro.value;
    const request = new ControleEstoqueInventarioPrintRequest();
    request.searchText = busca || undefined;
    request.idControleEstoqueListaRefinamento = idSituacaoProduto ?? 0;
    request.idTipoMovimentoFiltro = 0;
    request.idResumoFiltro = idExibir ?? 0;
    request.idLocalizacaoEstoque = idLocalizacaoEstoque ?? null;
    request.exibirSaldoAtual = !!apenasComEstoque;
    return request;
  }

  private achatarProdutos(lista: any[]): any[] {
    const resultado: any[] = [];
    for (const produto of lista ?? []) {
      if (produto?.variacoes && produto.variacoes.length > 0) {
        resultado.push(...produto.variacoes);
      } else {
        resultado.push(produto);
      }
    }
    return resultado;
  }

  private aplicarFiltroLocalizacao(): void {
    const { idLocalizacaoEstoque } = this.formFiltro.value;
    const locFiltro: number | null = idLocalizacaoEstoque;
    const rows: ProdutoFiltradoRow[] = [];

    // Constrói uma linha (produto ou variação) com os locais filtrados pela localização escolhida.
    // Retorna null quando há filtro de localização e o produto/variação não possui aquele local.
    const construirRow = (produto: any): ProdutoFiltradoRow | null => {
      const locais: Array<{ idLocalizacaoEstoque: number; saldoFisico: number; saldoDisponivel: number }> = produto.locais ?? [];
      const locaisFiltrados = locFiltro ? locais.filter(l => l.idLocalizacaoEstoque === locFiltro) : locais;

      if (locFiltro && locaisFiltrados.length === 0) return null;

      const saldoTotal = locaisFiltrados.reduce((s, l) => s + (l.saldoFisico ?? 0), 0);
      const saldoDisponivelTotal = locaisFiltrados.reduce((s, l) => s + (l.saldoDisponivel ?? 0), 0);

      const locaisMapeados: ProdutoLocalRow[] = locaisFiltrados.map(l => ({
        idLocalizacaoEstoque: l.idLocalizacaoEstoque,
        nomeLocalizacao: this.listaLocalizacoes.find(loc => loc.id === l.idLocalizacaoEstoque)?.descricao ?? `#${l.idLocalizacaoEstoque}`,
        saldoFisico: l.saldoFisico ?? 0,
        saldoDisponivel: l.saldoDisponivel ?? 0,
      }));

      const nomeLocais = locaisMapeados.map(l => l.nomeLocalizacao).join(', ');

      return {
        idProduto: produto.idProduto,
        descricao: produto.descricao,
        codigoSku: produto.codigoSku,
        unidade: produto.unidade,
        custoMedio: produto.custoMedio ?? 0,
        isControlaLotes: produto.isControlaLotes,
        isControlaSeries: produto.isControlaSeries,
        localizacoes: nomeLocais || '—',
        saldoTotal,
        saldoDisponivelTotal,
        locais: locaisMapeados,
        isProdutoComVariacao: false,
        quantidadeVariacoes: 0,
        variacoes: [],
      };
    };

    for (const produto of this.todosProdutos) {
      if (produto?.variacoes && produto.variacoes.length > 0) {
        // Produto com variação: monta as variações (filtradas) e, se houver alguma, a linha-pai agregada.
        const variacoesRows: ProdutoFiltradoRow[] = [];
        for (const variacao of produto.variacoes) {
          const variacaoRow = construirRow(variacao);
          if (variacaoRow) variacoesRows.push(variacaoRow);
        }

        if (variacoesRows.length === 0) continue;

        const parentRow = construirRow(produto);
        if (!parentRow) continue;

        parentRow.isProdutoComVariacao = true;
        parentRow.quantidadeVariacoes = variacoesRows.length;
        parentRow.variacoes = variacoesRows;
        rows.push(parentRow);
      } else {
        const row = construirRow(produto);
        if (row) rows.push(row);
      }
    }

    this.dataSource = rows;
    this.expandedElement = null;
    this.expandedVariacoes.clear();
    this._reconstruirFlatDataSource();
  }

  private _reconstruirFlatDataSource(): void {
    const flat: DisplayRow[] = [];
    for (const produto of this.dataSource) {
      flat.push({ data: produto, isVariacao: false, isLocal: false });

      if (this.expandedElement !== produto) continue;

      if (produto.variacoes.length > 0) {
        // Nível 2: variações; Nível 3: locais de cada variação.
        for (const variacao of produto.variacoes) {
          flat.push({ data: variacao, variacao, isVariacao: true, isLocal: false });

          if (this.expandedVariacoes.has(variacao) && variacao.locais.length > 1) {
            for (const local of variacao.locais) {
              flat.push({ data: variacao, variacao, local, isVariacao: true, isLocal: true });
            }
          }
        }
      } else if (produto.locais.length > 1) {
        // Produto simples: nível 2 = locais.
        for (const local of produto.locais) {
          flat.push({ data: produto, local, isVariacao: false, isLocal: true });
        }
      }
    }
    this._flatDataSource.data = flat;
  }

  public toggleRow(row: DisplayRow): void {
    if (row.isLocal) return;

    // Linha de variação (nível 2): expande/colapsa os locais da variação.
    if (row.isVariacao) {
      if (row.data.locais.length <= 1) return;
      if (this.expandedVariacoes.has(row.data)) {
        this.expandedVariacoes.delete(row.data);
      } else {
        this.expandedVariacoes.add(row.data);
      }
      this._reconstruirFlatDataSource();
      return;
    }

    // Linha do produto-pai: expande variações (se houver) ou locais (produto simples).
    if (!this.isRowExpandivel(row)) return;
    this.expandedElement = this.expandedElement === row.data ? null : row.data;
    this.expandedVariacoes.clear();
    this._reconstruirFlatDataSource();
  }

  public isVariacaoExpandida(variacao: ProdutoFiltradoRow): boolean {
    return this.expandedVariacoes.has(variacao);
  }

  public isRowExpandivel(row: DisplayRow): boolean {
    if (row.isLocal) return false;
    if (row.isVariacao) return row.data.locais.length > 1;
    return row.data.variacoes.length > 0 || row.data.locais.length > 1;
  }

  public toggleMobileProduto(produto: ProdutoFiltradoRow): void {
    this.expandedElement = this.expandedElement === produto ? null : produto;
    this.expandedVariacoes.clear();
  }

  public toggleVariacaoLocais(variacao: ProdutoFiltradoRow): void {
    if (this.expandedVariacoes.has(variacao)) {
      this.expandedVariacoes.delete(variacao);
    } else {
      this.expandedVariacoes.add(variacao);
    }
  }

  public getLocalizacaoLabel(row: DisplayRow): string {
    if (row.isLocal) return row.local?.nomeLocalizacao ?? '';
    if (row.data.locais.length > 1) return `${row.data.locais.length} locais`;
    return row.data.localizacoes;
  }

  public getSaldo(row: DisplayRow): number {
    const exibirDisponivel = !!this.formFiltro?.get('apenasComEstoque')?.value;
    if (exibirDisponivel) {
      return row.isLocal ? (row.local?.saldoDisponivel ?? 0) : row.data.saldoDisponivelTotal;
    }
    return row.isLocal ? (row.local?.saldoFisico ?? 0) : row.data.saldoTotal;
  }

  private carregarLocalizacoes(): void {
    this._isLoadingLocalizacoes = true;
    this.lookupService.getLocalizacaoEstoqueSelect().subscribe({
      next: res => {
        this._isLoadingLocalizacoes = false;
        if (!res.isError) {
          this.listaLocalizacoes = res.list ?? [];
        }
      },
      error: () => { this._isLoadingLocalizacoes = false; },
    });
  }

  private carregarLookups(): void {
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaCategoria = res.listaCategoria || [];
          this.listaTag = res.listaTag || [];
        }
      },
    });
  }

  private carregarFornecedores(): void {
    this.pessoaLookupService.listPessoaSelect(undefined, TipoContatoEnum.Fornecedor).subscribe({
      next: res => {
        if (!res.isError) this.listaFornecedor = res.list ?? [];
      },
    });
  }

  public refinarPor(tipo: number): void {
    this.tipoRefinamento = tipo;
  }
}
